import { logInfo, logWarn } from '../config_and_helpers/logger';

const LOG_CONTEXT = 'storageTree';

/**
 * Lab storage hierarchy: room → unit → level (shelf/rack/drawer) → box.
 *
 * `kind` is the position in the tree. `subtype` is the concrete thing at that
 * position (`ult_freezer`, `drawer`, …). A box is a leaf: nothing goes inside it
 * in this slice.
 *
 * @typedef {'room'|'unit'|'level'|'box'} StorageKind
 * @typedef {Object} StorageNode
 * @property {string} id
 * @property {string|null} parent_id
 * @property {StorageKind} kind
 * @property {string} name
 * @property {string} [subtype] - Unit or level subtype; rooms and plain boxes have none
 * @property {number} [slot_count] - Set on a slotted box. Clicking the box opens this many slots
 * @property {number} [lot_count] - Lots stored on this node itself, not in the places under it
 */

/** Storage unit subtypes. UTC is an ultra-low-temperature (−80C) freezer. */
export const STORAGE_UNIT_TYPES = [
  { value: 'ult_freezer', label: 'ULT freezer (-80C)' },
  { value: 'freezer_20', label: 'Freezer (-20C)' },
  { value: 'refrigerator', label: 'Refrigerator' },
  { value: 'ln2_dewar', label: 'Liquid-nitrogen dewar' },
  { value: 'incubator', label: 'Incubator' },
  { value: 'cabinet', label: 'Cabinet' },
  { value: 'storage_shelves', label: 'Storage shelves' },
];

/** Subdivisions inside a unit. */
export const STORAGE_LEVEL_TYPES = [
  { value: 'shelf', label: 'Shelf' },
  { value: 'rack', label: 'Rack' },
  { value: 'drawer', label: 'Drawer' },
];

/**
 * Level subtypes each unit type can hold. A dewar holds racks; a cabinet holds
 * shelves or drawers. Storage shelves hold only shelves. Keeps the add form
 * from offering a drawer in a dewar.
 */
const LEVEL_TYPES_BY_UNIT = {
  ult_freezer: ['shelf', 'rack', 'drawer'],
  freezer_20: ['shelf', 'drawer'],
  refrigerator: ['shelf', 'drawer'],
  ln2_dewar: ['rack'],
  incubator: ['shelf'],
  cabinet: ['shelf', 'drawer'],
  storage_shelves: ['shelf'],
};

/**
 * Subdivision created in bulk when the unit itself is added.
 * Cooled storage, cabinets, and storage shelves get this. A dewar's only legal
 * child is a rack, so that count creates racks. An incubator is warm and is left out.
 */
const BULK_LEVEL_BY_UNIT = {
  ult_freezer: 'shelf',
  freezer_20: 'shelf',
  refrigerator: 'shelf',
  ln2_dewar: 'rack',
  cabinet: 'shelf',
  storage_shelves: 'shelf',
};

/** Upper bound for one "add with shelves" submit, so a typo cannot POST hundreds of nodes. */
export const MAX_BULK_LEVELS = 40;

/** Upper bound for one slotted box. A 10×10 cryobox is 100. */
export const MAX_BOX_SLOTS = 100;

export const STORAGE_KIND_LABELS = {
  room: 'Room',
  unit: 'Storage unit',
  area: 'Area',
  level: 'Shelf / rack / drawer',
  box: 'Box',
};

/** Header for a column of places, in the list-of-columns view. */
export const STORAGE_COLUMN_LABELS = {
  room: 'Rooms',
  unit: 'Storage units',
  area: 'Areas',
  level: 'Shelves, racks, drawers',
  box: 'Boxes',
};

const SUBTYPE_LABELS = [...STORAGE_UNIT_TYPES, ...STORAGE_LEVEL_TYPES].reduce(
  (acc, option) => ({ ...acc, [option.value]: option.label }),
  {}
);

/**
 * Human label for a node: its subtype when it has one, else the kind.
 *
 * @param {StorageNode|null|undefined} node
 * @returns {string}
 */
export function getStorageNodeTypeLabel(node) {
  if (!node) return '';
  if (node.subtype && SUBTYPE_LABELS[node.subtype]) {
    return SUBTYPE_LABELS[node.subtype];
  }
  return STORAGE_KIND_LABELS[node.kind] || '';
}

/**
 * Which kind of child a parent accepts. `null` parent is the root (rooms).
 * A box is a leaf, so it returns null.
 *
 * @param {StorageNode|null|undefined} parent
 * @returns {StorageKind|null}
 */
export function getChildKind(parent) {
  if (!parent) return 'room';
  switch (parent.kind) {
    case 'room':
      return 'unit';
    case 'unit':
      return 'level';
    case 'level':
      return 'box';
    default:
      return null;
  }
}

/**
 * Subtype options for the add form at this parent. Rooms and boxes have no
 * subtype, so the list is empty and the form only asks for a name.
 *
 * @param {StorageNode|null|undefined} parent
 * @returns {Array<{ value: string, label: string }>}
 */
export function getChildSubtypeOptions(parent) {
  const childKind = getChildKind(parent);
  if (childKind === 'unit') return STORAGE_UNIT_TYPES;
  if (childKind === 'level') {
    const allowed = LEVEL_TYPES_BY_UNIT[parent?.subtype] || [];
    return STORAGE_LEVEL_TYPES.filter(option => allowed.includes(option.value));
  }
  return [];
}

/**
 * True when this node can still hold children (a box cannot).
 *
 * @param {StorageNode|null|undefined} node
 * @returns {boolean}
 */
export function canHaveChildren(node) {
  return getChildKind(node) !== null;
}

/**
 * Ids of a node and everything inside it. A move into this set would be a cycle.
 *
 * @param {StorageNode[]} nodes
 * @param {string|number} nodeId
 * @returns {Set<string>}
 */
function collectSubtreeIds(nodes, nodeId) {
  const list = Array.isArray(nodes) ? nodes : [];
  const ids = new Set([String(nodeId)]);
  const pending = [String(nodeId)];
  while (pending.length) {
    const parentId = pending.pop();
    list.forEach(node => {
      if (!node || node.parent_id == null) return;
      const id = String(node.id);
      if (String(node.parent_id) === parentId && !ids.has(id)) {
        ids.add(id);
        pending.push(id);
      }
    });
  }
  return ids;
}

/**
 * Every destination of the right kind, including the current parent and any
 * parent that already uses this name. Wrong subtypes and places inside this
 * node are left out. `selectable` is false for the current parent and for a
 * name collision.
 *
 * @param {StorageNode[]} nodes
 * @param {StorageNode|null|undefined} node
 * @returns {Array<{ node: StorageNode, selectable: boolean, reason: 'current'|'name'|null }>}
 */
export function listMoveDestinations(nodes, node) {
  if (!node || node.kind === 'room') return [];
  const list = Array.isArray(nodes) ? nodes : [];
  const blocked = collectSubtreeIds(list, node.id);
  const name = String(node.name || '').trim().toLowerCase();
  const destinations = list
    .filter(candidate => {
      if (!candidate || blocked.has(String(candidate.id))) return false;
      if (getChildKind(candidate) !== node.kind) return false;
      if (node.kind === 'level') {
        const allowed = getChildSubtypeOptions(candidate).some(
          option => option.value === node.subtype
        );
        if (!allowed) return false;
      }
      return true;
    })
    .map(candidate => {
      const current = String(candidate.id) === String(node.parent_id);
      const taken = getChildren(list, candidate.id).some(
        child =>
          String(child.id) !== String(node.id) &&
          String(child.name || '').trim().toLowerCase() === name
      );
      let reason = null;
      if (current) reason = 'current';
      else if (taken) reason = 'name';
      return { node: candidate, selectable: reason == null, reason };
    });
  const coveredRooms = new Set();
  destinations.forEach(item => {
    if (item.node.kind === 'room') {
      coveredRooms.add(String(item.node.id));
      return;
    }
    const room = getNodePath(list, item.node.id).find(place => place.kind === 'room');
    if (room) coveredRooms.add(String(room.id));
  });
  list
    .filter(place => place && place.kind === 'room' && !coveredRooms.has(String(place.id)))
    .forEach(room => {
      destinations.push({ node: room, selectable: false, reason: 'no-place' });
    });
  logInfo(LOG_CONTEXT, 'move destinations listed', {
    nodeId: node.id,
    kind: node.kind,
    destinationCount: destinations.length,
    selectableCount: destinations.filter(item => item.selectable).length,
  });
  return destinations;
}

/**
 * Parents this place can move to. Rooms stay at the root. A destination must
 * accept this kind (and this level subtype), must not be inside the place, and
 * must not already have a sibling with the same name.
 *
 * @param {StorageNode[]} nodes
 * @param {StorageNode|null|undefined} node
 * @returns {StorageNode[]}
 */
export function listLegalMoveParents(nodes, node) {
  return listMoveDestinations(nodes, node)
    .filter(item => item.selectable)
    .map(item => item.node);
}

/**
 * Slot count stored on a box, or null when the box is not slotted.
 * Accepts a numeric string from the API.
 *
 * @param {StorageNode|null|undefined} node
 * @returns {number|null}
 */
export function readSlotCount(node) {
  if (!node || node.kind !== 'box' || node.slot_count == null || node.slot_count === '') {
    return null;
  }
  const count = typeof node.slot_count === 'number'
    ? node.slot_count
    : Number(String(node.slot_count).trim());
  if (!Number.isInteger(count) || count < 1 || count > MAX_BOX_SLOTS) return null;
  return count;
}

/**
 * True when clicking this box should open its slot chart.
 *
 * @param {StorageNode|null|undefined} node
 * @returns {boolean}
 */
export function isSlottedBox(node) {
  return readSlotCount(node) != null;
}

/**
 * Parse the slot count from the add form. Unchecked means a plain box.
 *
 * @param {string|number|null|undefined} value
 * @param {{ slotted?: boolean }} [options]
 * @returns {{ count: number|null, error: string|null }}
 */
export function parseBoxSlotCount(value, { slotted = false } = {}) {
  if (!slotted) return { count: null, error: null };
  if (value == null || value === '') {
    return { count: null, error: 'Enter how many slots' };
  }
  const count = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isInteger(count) || count < 1) {
    return { count: null, error: 'Enter how many slots' };
  }
  if (count > MAX_BOX_SLOTS) {
    return { count: null, error: `A box can have up to ${MAX_BOX_SLOTS} slots` };
  }
  return { count, error: null };
}

/**
 * Columns for a roughly square slot chart.
 *
 * @param {number} slotCount
 * @returns {number}
 */
export function boxSlotColumns(slotCount) {
  const count = Number(slotCount) || 0;
  if (count <= 1) return 1;
  return Math.ceil(Math.sqrt(count));
}

/**
 * One empty cell per slot. Items are not assigned yet, so `item` stays null.
 *
 * @param {number} slotCount
 * @returns {Array<{ index: number, item: null }>}
 */
export function buildBoxSlots(slotCount) {
  const count = Number(slotCount) || 0;
  if (!Number.isInteger(count) || count < 1) return [];
  const size = Math.min(count, MAX_BOX_SLOTS);
  return Array.from({ length: size }, (_, index) => ({
    index: index + 1,
    item: null,
  }));
}

/**
 * Keep a numeric `slot_count` on boxes loaded from the API. Drop a value that
 * is not a usable count so a bad payload cannot open a chart.
 *
 * @param {StorageNode} node
 * @returns {StorageNode}
 */
export function normalizeStorageNode(node) {
  if (!node || node.kind !== 'box') return node;
  const slotCount = readSlotCount(node);
  if (!slotCount) {
    if (node.slot_count == null) return node;
    const next = { ...node };
    delete next.slot_count;
    return next;
  }
  return { ...node, slot_count: slotCount };
}

/**
 * The create response is the node to keep. If the server does not echo
 * `slot_count` yet, keep the count the user entered for this session.
 *
 * @param {StorageNode} serverNode
 * @param {StorageNode} draftNode
 * @returns {StorageNode}
 */
export function mergeCreatedStorageNode(serverNode, draftNode) {
  if (!serverNode) return serverNode;
  const draftSlots = readSlotCount(draftNode);
  if (!draftSlots) return serverNode;
  const serverSlots = readSlotCount(serverNode);
  if (serverSlots) return { ...serverNode, slot_count: serverSlots };
  logWarn(LOG_CONTEXT, 'server omitted slot_count, keeping it for this session', {
    id: serverNode.id ?? null,
    slotCount: draftSlots,
  });
  return { ...serverNode, slot_count: draftSlots };
}

/**
 * Apply a box edit. The server id and name win when the response includes
 * them. `slot_count` follows what the user saved; if the response omits a
 * count the user asked for, that count is kept for this session.
 *
 * @param {StorageNode|null|undefined} serverNode
 * @param {StorageNode} draftNode
 * @returns {StorageNode}
 */
export function applyBoxEdit(serverNode, draftNode) {
  const next = {
    ...(serverNode || {}),
    ...draftNode,
    id: serverNode?.id ?? draftNode.id,
    kind: 'box',
    name: serverNode?.name || draftNode.name,
    parent_id: serverNode?.parent_id ?? draftNode.parent_id,
  };
  const wanted = readSlotCount(draftNode);
  const echoed = readSlotCount(serverNode);
  if (wanted) {
    next.slot_count = echoed || wanted;
    if (!echoed) {
      logWarn(LOG_CONTEXT, 'server omitted slot_count, keeping it for this session', {
        id: next.id ?? null,
        slotCount: wanted,
      });
    }
  } else {
    delete next.slot_count;
  }
  return next;
}

/**
 * Change an existing box's name and, when it is slotted, its slot count.
 * Unchecking slotted clears `slot_count`. The list is unchanged on error.
 *
 * @param {Object} args
 * @param {StorageNode[]} args.nodes
 * @param {StorageNode} args.box
 * @param {string} args.name
 * @param {boolean} [args.slotted]
 * @param {string|number} [args.slotCount]
 * @returns {{ nodes: StorageNode[], node: StorageNode|null, error: string|null }}
 */
export function updateStorageBox({ nodes, box, name, slotted = false, slotCount } = {}) {
  const list = Array.isArray(nodes) ? nodes : [];
  if (!box || box.kind !== 'box') {
    return { nodes: list, node: null, error: 'Only a box can be edited this way' };
  }
  const current = findNode(list, box.id);
  if (!current || current.kind !== 'box') {
    return { nodes: list, node: null, error: 'That box is no longer on the map' };
  }

  const trimmedName = String(name || '').trim();
  if (!trimmedName) {
    return { nodes: list, node: null, error: 'Enter a name' };
  }

  const duplicate = getChildren(list, current.parent_id).some(
    child =>
      child &&
      String(child.id) !== String(current.id) &&
      child.name.trim().toLowerCase() === trimmedName.toLowerCase()
  );
  if (duplicate) {
    return { nodes: list, node: null, error: 'That name is already used here' };
  }

  const parsedSlots = parseBoxSlotCount(slotCount, { slotted: Boolean(slotted) });
  if (parsedSlots.error) {
    return { nodes: list, node: null, error: parsedSlots.error };
  }

  const next = { ...current, name: trimmedName };
  if (parsedSlots.count) next.slot_count = parsedSlots.count;
  else delete next.slot_count;

  logInfo(LOG_CONTEXT, 'storage box updated', {
    id: current.id,
    slotted: Boolean(parsedSlots.count),
    slotCount: parsedSlots.count,
  });

  return {
    nodes: list.map(node =>
      node && String(node.id) === String(current.id) ? next : node
    ),
    node: next,
    error: null,
  };
}

/**
 * Level subtype created together with this unit, or null when the unit is not
 * cooled storage or a cabinet (an incubator has no bulk count).
 *
 * @param {string|null|undefined} unitSubtype
 * @returns {'shelf'|'rack'|null}
 */
export function getBulkLevelSubtype(unitSubtype) {
  return BULK_LEVEL_BY_UNIT[unitSubtype] || null;
}

/**
 * Counts the add form should ask for when creating this unit.
 * A unit that holds both shelves and drawers asks for each. A dewar asks for
 * racks. Storage shelves ask for shelves. An incubator asks for none.
 *
 * @param {string|null|undefined} unitSubtype
 * @returns {Array<'shelf'|'drawer'|'rack'>}
 */
export function getBulkLevelOptions(unitSubtype) {
  const allowed = LEVEL_TYPES_BY_UNIT[unitSubtype] || [];
  if (
    BULK_LEVEL_BY_UNIT[unitSubtype] &&
    allowed.includes('shelf') &&
    allowed.includes('drawer')
  ) {
    return ['shelf', 'drawer'];
  }
  const single = getBulkLevelSubtype(unitSubtype);
  return single ? [single] : [];
}

/**
 * Parse the shelves/racks count from the add form. Blank means none.
 *
 * @param {string|number|null|undefined} value
 * @returns {{ count: number, error: string|null }}
 */
export function parseBulkLevelCount(value) {
  if (value == null || value === '') return { count: 0, error: null };
  const count = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isInteger(count) || count < 0) {
    return { count: 0, error: 'Enter a whole number' };
  }
  if (count > MAX_BULK_LEVELS) {
    return { count: 0, error: `Add up to ${MAX_BULK_LEVELS} at a time` };
  }
  return { count, error: null };
}

/**
 * Names for the shelves (or racks) created with a unit: "Shelf 1", "Shelf 2", …
 * Skips names already used under that parent.
 *
 * @param {string} unitSubtype
 * @param {string|number} count
 * @param {string[]} [existingNames]
 * @returns {string[]}
 */
/**
 * "Shelf 1" / "Drawer 1" / "Rack 1" for a known level subtype.
 *
 * @param {'shelf'|'drawer'|'rack'} levelSubtype
 * @param {string|number} count
 * @param {string[]} [existingNames]
 * @returns {string[]}
 */
export function buildLevelNames(levelSubtype, count, existingNames = []) {
  const parsed = parseBulkLevelCount(count);
  if (!levelSubtype || parsed.error || parsed.count === 0) return [];
  const label =
    levelSubtype === 'rack' ? 'Rack' : levelSubtype === 'drawer' ? 'Drawer' : 'Shelf';
  const taken = new Set(
    (existingNames || []).map(name => String(name || '').trim().toLowerCase())
  );
  const names = [];
  let index = 1;
  const indexLimit = parsed.count + taken.size + 1;
  while (names.length < parsed.count && index <= indexLimit) {
    const candidate = `${label} ${index}`;
    index += 1;
    if (taken.has(candidate.toLowerCase())) continue;
    taken.add(candidate.toLowerCase());
    names.push(candidate);
  }
  return names;
}

export function buildBulkLevelNames(unitSubtype, count, existingNames = []) {
  const subtype = getBulkLevelSubtype(unitSubtype);
  const parsed = parseBulkLevelCount(count);
  if (!subtype || parsed.error || parsed.count === 0) return [];

  const label = subtype === 'rack' ? 'Rack' : 'Shelf';
  const taken = new Set(
    (existingNames || []).map(name => String(name || '').trim().toLowerCase())
  );
  const names = [];
  let index = 1;
  const indexLimit = parsed.count + taken.size + 1;
  while (names.length < parsed.count && index <= indexLimit) {
    const candidate = `${label} ${index}`;
    index += 1;
    if (taken.has(candidate.toLowerCase())) continue;
    taken.add(candidate.toLowerCase());
    names.push(candidate);
  }
  return names;
}

/**
 * Boxes at or under a node. A box returns itself. A room, unit, or shelf
 * returns every box nested inside it.
 *
 * @param {StorageNode[]} nodes
 * @param {string|number|null|undefined} nodeId
 * @returns {StorageNode[]}
 */
export function listDescendantBoxes(nodes, nodeId) {
  const start = findNode(nodes, nodeId);
  if (!start) return [];
  if (start.kind === 'box') return [start];
  const boxes = [];
  const walk = id => {
    getChildren(nodes, id).forEach(child => {
      if (child.kind === 'box') boxes.push(child);
      else walk(child.id);
    });
  };
  walk(start.id);
  return boxes;
}

/**
 * Nodes whose contents should be loaded for a place. A box or a level includes
 * itself. A room or unit includes the shelves, drawers, racks, and boxes under
 * it. Contents of a level are only the lots on that level, so child boxes are
 * requested separately.
 *
 * @param {StorageNode[]} nodes
 * @param {string|number|null|undefined} nodeId
 * @returns {StorageNode[]}
 */
export function listStorageContentNodes(nodes, nodeId) {
  const start = findNode(nodes, nodeId);
  if (!start) return [];
  const found = [];
  const consider = node => {
    if (node && (node.kind === 'box' || node.kind === 'level')) found.push(node);
  };
  consider(start);
  if (start.kind === 'box') return found;
  const walk = id => {
    getChildren(nodes, id).forEach(child => {
      consider(child);
      if (child.kind !== 'box') walk(child.id);
    });
  };
  walk(start.id);
  logInfo(LOG_CONTEXT, 'storage content nodes listed', {
    nodeId: start.id,
    kind: start.kind,
    targetCount: found.length,
  });
  return found;
}

/**
 * Direct children of a parent, in insertion order.
 *
 * @param {StorageNode[]} nodes
 * @param {string|null} parentId
 * @returns {StorageNode[]}
 */
export function getChildren(nodes, parentId = null) {
  const list = Array.isArray(nodes) ? nodes : [];
  return list.filter(node => {
    if (!node) return false;
    if (parentId == null) return node.parent_id == null;
    return String(node.parent_id) === String(parentId);
  });
}

/**
 * How many direct children a node has.
 *
 * @param {StorageNode[]} nodes
 * @param {string} nodeId
 * @returns {number}
 */
export function countChildren(nodes, nodeId) {
  return getChildren(nodes, nodeId).length;
}

/**
 * Lots stored on this node itself. Missing, blank, or not a whole number is
 * unknown — never zero. Zero is a real empty place.
 *
 * @param {StorageNode|null|undefined} node
 * @returns {number|null}
 */
export function readDirectLotCount(node) {
  if (!node || !Object.prototype.hasOwnProperty.call(node, 'lot_count')) return null;
  const raw = node.lot_count;
  if (typeof raw === 'boolean' || raw == null || raw === '') return null;
  const count = Number(raw);
  if (!Number.isInteger(count) || count < 0) return null;
  return count;
}

/**
 * Direct lot counts for shelves, drawers, racks, and boxes. Rooms and units
 * are skipped. When no place has `lot_count`, the result is null so cards can
 * fall back to how many places are inside them.
 *
 * @param {StorageNode[]} nodes
 * @returns {Object<string, number>|null}
 */
export function directLotCountsFromNodes(nodes) {
  const counts = {};
  let seen = false;
  (Array.isArray(nodes) ? nodes : []).forEach(node => {
    if (!node || (node.kind !== 'level' && node.kind !== 'box')) return;
    const count = readDirectLotCount(node);
    if (count == null) return;
    seen = true;
    counts[String(node.id)] = count;
  });
  if (!seen) return null;
  logInfo(LOG_CONTEXT, 'lot counts read from the tree', {
    placeCount: Object.keys(counts).length,
  });
  return counts;
}

/**
 * Stock lots on this place and in the places under it. `directCounts` maps a
 * node id to the number of lots stored on that node itself. A place that is
 * missing from that map makes the total unknown (`null`), not zero.
 *
 * @param {StorageNode[]} nodes
 * @param {string|number} nodeId
 * @param {Object<string, number>|null|undefined} directCounts
 * @returns {number|null}
 */
export function countLotsInside(nodes, nodeId, directCounts) {
  if (!directCounts) return null;
  const targets = listStorageContentNodes(nodes, nodeId);
  if (!targets.length) return 0;
  let sum = 0;
  for (let index = 0; index < targets.length; index += 1) {
    const direct = directCounts[String(targets[index].id)];
    if (!Number.isFinite(direct)) return null;
    sum += direct;
  }
  return sum;
}

/**
 * Count line for a place, on a map card or a list row. A slotted box leads with
 * its slots. A place that cannot hold anything and has no lot count is the end
 * of the path. `lotCount` of null means unknown, so the child count is shown.
 *
 * @param {Object} args
 * @param {StorageNode|null|undefined} args.node
 * @param {number} [args.childCount]
 * @param {number|null} [args.lotCount]
 * @returns {string}
 */
export function formatStorageNodeCountLabel({ node, childCount = 0, lotCount = null } = {}) {
  const shown = lotCount == null ? childCount : lotCount;
  const itemsLabel = `${shown} ${shown === 1 ? 'item' : 'items'} inside`;
  const slots = readSlotCount(node);
  if (slots) {
    const slotsLabel = `${slots} ${slots === 1 ? 'slot' : 'slots'}`;
    return lotCount == null ? slotsLabel : `${slotsLabel} · ${itemsLabel}`;
  }
  if (canHaveChildren(node) || lotCount != null) return itemsLabel;
  return 'End of the path';
}

/**
 * Card counts after lots move. Each lot leaves its source place and is added
 * to the destination. Other places are left as they were.
 *
 * @param {Object<string, number>|null|undefined} counts
 * @param {{ moved?: Array, storageNodeId?: string|number|null }} change
 * @returns {Object<string, number>}
 */
export function applyMovedLotCounts(counts, change = {}) {
  const next = { ...(counts || {}) };
  const lots = Array.isArray(change.moved) ? change.moved : [];
  lots.forEach(lot => {
    const from = lot?.boxId ?? lot?.storage_node_id;
    if (from == null || from === '') return;
    const key = String(from);
    next[key] = Math.max(0, (Number(next[key]) || 0) - 1);
  });
  const destination = change.storageNodeId;
  if (destination != null && destination !== '' && lots.length) {
    const key = String(destination);
    next[key] = (Number(next[key]) || 0) + lots.length;
  }
  logInfo(LOG_CONTEXT, 'lot counts adjusted for a move', {
    moved: lots.length,
    storageNodeId: destination ?? null,
  });
  return next;
}

/**
 * Resolve a node by id.
 *
 * @param {StorageNode[]} nodes
 * @param {string|null|undefined} nodeId
 * @returns {StorageNode|null}
 */
export function findNode(nodes, nodeId) {
  if (nodeId == null) return null;
  const list = Array.isArray(nodes) ? nodes : [];
  return list.find(node => node && String(node.id) === String(nodeId)) || null;
}

/**
 * Ancestor chain for a node, root first, including the node itself.
 * Used for the breadcrumb. Guards against a parent cycle from a bad payload.
 *
 * @param {StorageNode[]} nodes
 * @param {string|null|undefined} nodeId
 * @returns {StorageNode[]}
 */
export function getNodePath(nodes, nodeId) {
  const path = [];
  const seen = new Set();
  let current = findNode(nodes, nodeId);
  while (current) {
    if (seen.has(String(current.id))) {
      logWarn(LOG_CONTEXT, 'parent cycle detected, truncating path', {
        nodeId: current.id,
      });
      break;
    }
    seen.add(String(current.id));
    path.unshift(current);
    current = findNode(nodes, current.parent_id);
  }
  return path;
}

/**
 * Ids of a node's ancestors, root first, without the node itself. Used to open
 * the places above a row so it can be seen.
 *
 * @param {StorageNode[]} nodes
 * @param {string|number|null|undefined} nodeId
 * @returns {string[]}
 */
export function listAncestorIds(nodes, nodeId) {
  return getNodePath(nodes, nodeId)
    .slice(0, -1)
    .map(node => String(node.id));
}

/**
 * Normalize ids (a Set or an array, numbers or strings) to a Set of strings.
 *
 * @param {Set|Array|null|undefined} ids
 * @returns {Set<string>}
 */
function toStorageIdSet(ids) {
  const set = new Set();
  if (!ids) return set;
  const source = ids instanceof Set ? Array.from(ids) : Array.isArray(ids) ? ids : [];
  source.forEach(id => {
    if (id == null || id === '') return;
    set.add(String(id));
  });
  return set;
}

/**
 * Every place reachable from a root by walking children. A node whose parent is
 * missing, or that sits in a parent cycle, is left out.
 *
 * @param {StorageNode[]} nodes
 * @returns {Set<string>}
 */
function reachableStorageIds(nodes) {
  const list = Array.isArray(nodes) ? nodes : [];
  const reachable = new Set();
  const queue = getChildren(list, null).filter(Boolean);
  while (queue.length) {
    const node = queue.shift();
    const key = String(node.id);
    if (reachable.has(key)) continue;
    reachable.add(key);
    getChildren(list, node.id).forEach(child => {
      if (child) queue.push(child);
    });
  }
  return reachable;
}

/**
 * Places a list search should show. A name match keeps the places above it, so
 * the path stays readable, and the places under it, so what is inside is still
 * listed. An empty search means no filter at all (`visibleIds` is null).
 *
 * Same rule as the place picker's `filterStoragePlaceOptions`: searching a
 * freezer lists its drawers and boxes, and searching a drawer still finds it.
 *
 * @param {StorageNode[]} nodes
 * @param {string} query
 * @returns {{ visibleIds: Set<string>|null, matchedIds: Set<string> }}
 */
export function matchStorageTree(nodes, query) {
  const text = String(query || '')
    .trim()
    .toLowerCase();
  const list = Array.isArray(nodes) ? nodes : [];
  if (!text) return { visibleIds: null, matchedIds: new Set() };

  const matchedIds = new Set();
  list.forEach(node => {
    if (!node || node.id == null) return;
    if (String(node.name || '').toLowerCase().includes(text)) {
      matchedIds.add(String(node.id));
    }
  });

  const visibleIds = new Set();
  matchedIds.forEach(id => {
    listAncestorIds(list, id).forEach(ancestorId => visibleIds.add(ancestorId));
    const queue = [id];
    const walked = new Set();
    while (queue.length) {
      const current = queue.shift();
      if (walked.has(current)) continue;
      walked.add(current);
      visibleIds.add(current);
      getChildren(list, current).forEach(child => {
        if (child && child.id != null) queue.push(String(child.id));
      });
    }
  });

  return { visibleIds, matchedIds };
}

/**
 * Flat rows for the list view, depth first, each place before the places inside
 * it. A closed place contributes one row and its children are left out.
 *
 * `visibleIds` from `matchStorageTree` narrows the rows to a search; null shows
 * the whole tree. A place whose parent is missing, or that sits in a cycle, is
 * listed at the top level so it cannot disappear — same as the map layout.
 *
 * @param {StorageNode[]} nodes
 * @param {{ expandedIds?: Set|Array, visibleIds?: Set|Array|null }} [options]
 * @returns {Array<{ id: string, node: StorageNode, depth: number, hasChildren: boolean, expanded: boolean }>}
 */
export function buildStorageListRows(nodes, options = {}) {
  const list = Array.isArray(nodes) ? nodes : [];
  const expanded = toStorageIdSet(options.expandedIds);
  const visible = options.visibleIds == null ? null : toStorageIdSet(options.visibleIds);
  const shown = node =>
    Boolean(node) && node.id != null && (visible == null || visible.has(String(node.id)));

  const rows = [];
  const seen = new Set();

  const walk = (node, depth) => {
    const key = String(node.id);
    if (seen.has(key)) {
      logWarn(LOG_CONTEXT, 'parent cycle detected, dropping repeated list row', {
        nodeId: node.id,
      });
      return;
    }
    seen.add(key);
    const children = getChildren(list, node.id).filter(
      child => shown(child) && !seen.has(String(child.id))
    );
    const isExpanded = children.length > 0 && expanded.has(key);
    rows.push({ id: key, node, depth, hasChildren: children.length > 0, expanded: isExpanded });
    if (!isExpanded) return;
    children.forEach(child => walk(child, depth + 1));
  };

  getChildren(list, null)
    .filter(shown)
    .forEach(node => walk(node, 0));

  const reachable = reachableStorageIds(list);
  const strays = list.filter(node => shown(node) && !reachable.has(String(node.id)));
  if (strays.length) {
    logWarn(LOG_CONTEXT, 'nodes not reachable from a root, listed at the top level', {
      orphanCount: strays.length,
    });
    strays.forEach(node => {
      if (seen.has(String(node.id))) return;
      walk(node, 0);
    });
  }

  return rows;
}

/**
 * Columns for the left-to-right view, one per step down to the selected place.
 *
 * The first column is the rooms. Each place on the way to `selectedId` that can
 * hold something adds the next column, so clicking a room fills the units
 * beside it. A box is a leaf and ends the chain. The hierarchy is bounded at
 * room → unit → level → box, so there are never more than four columns.
 *
 * An empty column is still returned, so the view can say a place has nothing in
 * it yet instead of silently ending. An id that is not in the tree leaves just
 * the rooms, and `getNodePath` already truncates a parent cycle.
 *
 * @param {StorageNode[]} nodes
 * @param {string|number|null|undefined} selectedId
 * @returns {Array<{ parentId: string|null, parent: StorageNode|null, childKind: StorageKind, label: string, nodes: StorageNode[], selectedId: string|null }>}
 */
export function buildStorageColumns(nodes, selectedId) {
  const list = Array.isArray(nodes) ? nodes : [];
  const path = getNodePath(list, selectedId);

  /**
   * @param {StorageNode|null} parent
   * @param {StorageNode|null|undefined} next - The place on the path inside this column
   * @returns {Object|null}
   */
  const column = (parent, next) => {
    const childKind = getChildKind(parent);
    if (!childKind) return null;
    return {
      parentId: parent ? String(parent.id) : null,
      parent: parent || null,
      childKind,
      label: STORAGE_COLUMN_LABELS[childKind] || STORAGE_KIND_LABELS[childKind] || '',
      nodes: getChildren(list, parent ? parent.id : null),
      selectedId: next && next.id != null ? String(next.id) : null,
    };
  };

  const columns = [column(null, path[0])].filter(Boolean);
  path.forEach((place, index) => {
    const next = column(place, path[index + 1]);
    if (next) columns.push(next);
  });

  logInfo(LOG_CONTEXT, 'storage columns built', {
    columnCount: columns.length,
    depth: path.length,
  });

  return columns;
}

/**
 * Flowchart spacing, in flow units.
 * Siblings sit side by side (`COLUMN_WIDTH` is wider than a 14rem card).
 * Each generation sits below its parent (`ROW_HEIGHT`). An open add form
 * still pushes the cards under it by the measured extra height.
 */
const COLUMN_WIDTH = 300;
const ROW_HEIGHT = 320;

/** Card width in flow units. Matches `.storage-node` (14rem). Used to see which cards sit under an open form. */
export const STORAGE_CARD_WIDTH = 224;

/** Space left between an open form and the next card. */
const OPEN_FORM_GAP = 12;

/**
 * How far to move a card down while a card above it has its add or edit form
 * open. A card counts as underneath when its left edge is within one card
 * width of the open card. The open card itself does not move.
 *
 * @param {{ x: number, y: number }} position - Unshifted position of this card
 * @param {string} nodeId
 * @param {Array<{ id: string, x: number, y: number, extra: number }>} openings
 * @returns {number}
 */
export function storageNodeShiftY(position, nodeId, openings) {
  if (!position || !Array.isArray(openings)) return 0;
  return openings.reduce((shift, opening) => {
    if (!opening || String(opening.id) === String(nodeId) || !opening.extra) return shift;
    const overlapsX = Math.abs(position.x - opening.x) < STORAGE_CARD_WIDTH;
    const below = position.y > opening.y + 4;
    if (overlapsX && below) return shift + opening.extra + OPEN_FORM_GAP;
    return shift;
  }, 0);
}

/**
 * Place the tree as a top-to-bottom flowchart: one row per depth (rooms on
 * top, boxes below), siblings spread side by side. A parent sits centered
 * against the span of its children, which is what makes it read as a tree
 * rather than a grid.
 *
 * These are only starting positions. Once the map is on screen the user drags
 * nodes freely, and those positions are not persisted.
 *
 * @param {StorageNode[]} nodes
 * @param {{ columnWidth?: number, rowHeight?: number }} [options]
 * @returns {{ nodes: Array<{ id: string, depth: number, node: StorageNode, position: { x: number, y: number } }>, edges: Array<{ id: string, source: string, target: string }> }}
 */
export function buildStorageFlowLayout(nodes, options = {}) {
  const columnWidth = options.columnWidth ?? COLUMN_WIDTH;
  const rowHeight = options.rowHeight ?? ROW_HEIGHT;
  const list = Array.isArray(nodes) ? nodes : [];

  const placed = [];
  const visited = new Set();
  let nextColumn = 0;

  /**
   * Depth-first placement. Returns the x this node was given so the parent can
   * center itself between its first and last child.
   *
   * @param {StorageNode} node
   * @param {number} depth
   * @returns {number}
   */
  const place = (node, depth) => {
    const key = String(node.id);
    visited.add(key);
    const children = getChildren(list, node.id).filter(
      child => child && !visited.has(String(child.id))
    );

    let x;
    if (!children.length) {
      x = nextColumn * columnWidth;
      nextColumn += 1;
    } else {
      const childXs = children.map(child => place(child, depth + 1));
      x = (childXs[0] + childXs[childXs.length - 1]) / 2;
    }

    placed.push({
      id: key,
      depth,
      node,
      position: { x, y: depth * rowHeight },
    });
    return x;
  };

  getChildren(list, null).forEach(root => {
    if (root && !visited.has(String(root.id))) place(root, 0);
  });

  // Any node left over sits under a parent that is missing or in a cycle.
  const orphans = list.filter(node => node && !visited.has(String(node.id)));
  if (orphans.length) {
    logWarn(LOG_CONTEXT, 'nodes not reachable from a root', {
      orphanCount: orphans.length,
    });
    orphans.forEach(node => {
      placed.push({
        id: String(node.id),
        depth: 0,
        node,
        position: { x: nextColumn * columnWidth, y: 0 },
      });
      nextColumn += 1;
      visited.add(String(node.id));
    });
  }

  logInfo(LOG_CONTEXT, 'storage flow layout built', {
    nodeCount: placed.length,
    orientation: 'vertical',
  });

  const known = new Set(placed.map(item => item.id));
  const edges = list
    .filter(
      node =>
        node &&
        node.parent_id != null &&
        known.has(String(node.parent_id)) &&
        known.has(String(node.id))
    )
    .map(node => ({
      id: `${node.parent_id}-${node.id}`,
      source: String(node.parent_id),
      target: String(node.id),
    }));

  return { nodes: placed, edges };
}

let idCounter = 0;

/**
 * Client-side id for a node that has not been saved yet.
 *
 * @returns {string}
 */
function nextLocalId() {
  idCounter += 1;
  return `local-${Date.now().toString(36)}-${idCounter}`;
}

/**
 * Add a child under `parent`. Returns the unchanged list when the placement is
 * not allowed (no child kind, missing name, or a subtype that does not fit the
 * parent), so a bad add cannot corrupt the tree.
 *
 * @param {Object} args
 * @param {StorageNode[]} args.nodes
 * @param {StorageNode|null} [args.parent] - Null adds a room at the root
 * @param {string} args.name
 * @param {string} [args.subtype]
 * @param {boolean} [args.slotted] - Box only. Asks for `slotCount`
 * @param {string|number} [args.slotCount]
 * @returns {{ nodes: StorageNode[], node: StorageNode|null, error: string|null }}
 */
export function addStorageNode({
  nodes,
  parent = null,
  name,
  subtype,
  slotted = false,
  slotCount,
} = {}) {
  const list = Array.isArray(nodes) ? nodes : [];
  const childKind = getChildKind(parent);

  if (!childKind) {
    return {
      nodes: list,
      node: null,
      error: `${getStorageNodeTypeLabel(parent)} cannot contain anything else`,
    };
  }

  const trimmedName = String(name || '').trim();
  if (!trimmedName) {
    return { nodes: list, node: null, error: 'Enter a name' };
  }

  const options = getChildSubtypeOptions(parent);
  let resolvedSubtype;
  if (options.length) {
    resolvedSubtype = subtype || options[0].value;
    if (!options.some(option => option.value === resolvedSubtype)) {
      return {
        nodes: list,
        node: null,
        error: 'That type does not fit here',
      };
    }
  }

  const duplicate = getChildren(list, parent?.id ?? null).some(
    child => child.name.trim().toLowerCase() === trimmedName.toLowerCase()
  );
  if (duplicate) {
    return { nodes: list, node: null, error: 'That name is already used here' };
  }

  let resolvedSlotCount = null;
  if (childKind === 'box') {
    const parsedSlots = parseBoxSlotCount(slotCount, { slotted: Boolean(slotted) });
    if (parsedSlots.error) {
      return { nodes: list, node: null, error: parsedSlots.error };
    }
    resolvedSlotCount = parsedSlots.count;
  }

  const node = {
    id: nextLocalId(),
    parent_id: parent?.id ?? null,
    kind: childKind,
    name: trimmedName,
    ...(resolvedSubtype ? { subtype: resolvedSubtype } : {}),
    ...(resolvedSlotCount ? { slot_count: resolvedSlotCount } : {}),
  };

  logInfo(LOG_CONTEXT, 'storage node added', {
    id: node.id,
    parentId: node.parent_id,
    kind: node.kind,
    subtype: node.subtype ?? null,
    slotCount: resolvedSlotCount,
  });

  return { nodes: [...list, node], node, error: null };
}

/**
 * Add a storage unit and, for cooled storage or a cabinet, several shelves
 * (racks for a liquid-nitrogen dewar) in the same step. A count of 0, or a unit
 * that is not in that set (an incubator), adds only the unit.
 *
 * On any validation failure the original list is returned unchanged.
 *
 * @param {Object} args
 * @param {StorageNode[]} args.nodes
 * @param {StorageNode|null} [args.parent]
 * @param {string} args.name
 * @param {string} [args.subtype]
 * @param {string|number} [args.levelCount] - Shelves, or racks for a dewar, when the form has one count
 * @param {string|number} [args.shelfCount]
 * @param {string|number} [args.drawerCount]
 * @param {boolean} [args.slotted]
 * @param {string|number} [args.slotCount]
 * @returns {{ nodes: StorageNode[], node: StorageNode|null, levels: StorageNode[], error: string|null }}
 */
export function addStorageUnitWithLevels({
  nodes,
  parent = null,
  name,
  subtype,
  levelCount = 0,
  shelfCount,
  drawerCount,
  slotted = false,
  slotCount,
} = {}) {
  const list = Array.isArray(nodes) ? nodes : [];
  const options = getBulkLevelOptions(subtype);
  const wantsBoth = options.includes('shelf') && options.includes('drawer');
  const shelfSource = wantsBoth
    ? shelfCount ?? levelCount
    : options[0] === 'shelf'
      ? shelfCount ?? levelCount
      : 0;
  const drawerSource = wantsBoth ? drawerCount ?? 0 : 0;
  const rackSource = options[0] === 'rack' ? levelCount : 0;
  const shelfParsed = parseBulkLevelCount(shelfSource);
  const drawerParsed = parseBulkLevelCount(drawerSource);
  const rackParsed = parseBulkLevelCount(rackSource);
  const countError = shelfParsed.error || drawerParsed.error || rackParsed.error;
  if (countError) {
    return { nodes: list, node: null, levels: [], error: countError };
  }

  const unit = addStorageNode({
    nodes: list,
    parent,
    name,
    subtype,
    slotted,
    slotCount,
  });
  if (!unit.node) {
    return { nodes: unit.nodes, node: null, levels: [], error: unit.error };
  }

  const batches = [
    ['shelf', shelfParsed.count],
    ['drawer', drawerParsed.count],
    ['rack', rackParsed.count],
  ].filter(([, count]) => count > 0);
  if (!batches.length) {
    return { nodes: unit.nodes, node: unit.node, levels: [], error: null };
  }

  let next = unit.nodes;
  const levels = [];
  for (const [levelSubtype, count] of batches) {
    const names = buildLevelNames(levelSubtype, count);
    for (const levelName of names) {
      const level = addStorageNode({
        nodes: next,
        parent: unit.node,
        name: levelName,
        subtype: levelSubtype,
      });
      if (!level.node) {
        logWarn(LOG_CONTEXT, 'bulk level add failed, unit not kept', {
          unitSubtype: unit.node.subtype,
          levelName,
          levelSubtype,
          error: level.error,
        });
        return { nodes: list, node: null, levels: [], error: level.error };
      }
      next = level.nodes;
      levels.push(level.node);
    }
  }

  logInfo(LOG_CONTEXT, 'storage unit added with levels', {
    unitId: unit.node.id,
    unitSubtype: unit.node.subtype,
    shelfCount: shelfParsed.count,
    drawerCount: drawerParsed.count,
    rackCount: rackParsed.count,
    levelCount: levels.length,
  });

  return { nodes: next, node: unit.node, levels, error: null };
}

/**
 * Remove a node and everything under it.
 *
 * @param {StorageNode[]} nodes
 * @param {string} nodeId
 * @returns {StorageNode[]}
 */
export function removeStorageNode(nodes, nodeId) {
  const list = Array.isArray(nodes) ? nodes : [];
  const target = findNode(list, nodeId);
  if (!target) return list;

  // Walk down from the target, collecting every descendant id.
  const doomed = new Set([String(target.id)]);
  const pending = [String(target.id)];
  while (pending.length) {
    const parentId = pending.pop();
    for (const node of list) {
      if (!node || node.parent_id == null) continue;
      const nodeId = String(node.id);
      if (String(node.parent_id) === parentId && !doomed.has(nodeId)) {
        doomed.add(nodeId);
        pending.push(nodeId);
      }
    }
  }

  logInfo(LOG_CONTEXT, 'storage node removed', {
    id: target.id,
    kind: target.kind,
    removedCount: doomed.size,
  });

  return list.filter(node => !doomed.has(String(node.id)));
}
